import { locationPermissionAtom, locationFallbackAtom } from "@/atoms/recommendationAtom";
import {
  hasActiveFiltersAtom,
  scoredRecommendationQueryParamsAtom,
  sortOptionAtom
} from "@/atoms/recommendationFilterAtom";
import { userAtom } from "@/atoms/userAtom";
import { AiLevelDisplay } from "@/common/AiLevelDisplay";
import { AuthenticationError } from "@/common/AuthenticationError";
import { LocationFallbackBanner } from "@/common/LocationFallbackBanner";
import { LocationPermissionModal } from "@/common/LocationPermissionModal";
import { trackRequestRecommendation } from "@/services/analytics";
import { isAuthenticated } from "@/utils/authErrors";
import { useAtom, useAtomValue } from "jotai";
import { MapPin, SlidersHorizontalIcon, Settings } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDemoFetcherWithSwr, useDemoSender } from "../api";
import { useModalRouting } from "../hooks/useModalRouting";
import { useLineConnection } from "../hooks/useLineConnection";
import { useOnboardingTour, useReopenOnboardingTour } from "../common/OnboardingTour";
import { debugModeAtom } from "../atoms/debugModeAtom";
import { LocationErrorDialog } from "../common/LocationErrorDialog";
import { PageWrapper } from "../common/PageWrapper";
import { RecommendationFilterDrawer } from "../common/RecommendationFilterDrawer";
import { SettingsMenu } from "../common/SettingsMenu";
import { UserMenuModal } from "../common/UserMenuModal";
import { LineDeliverySettingsModal } from "../common/LineDeliverySettingsModal";
import { LineConnectionResultModal } from "../common/LineConnectionResultModal";
import { SwipeableCard } from "../common/SwipableCard";
import { Button } from "../components/ui/button";
import type { RecommendationResult } from "../generatedApi/spotSearchEngineApi";

export const HomePage = () => {
  const {
    data: version,
    isLoading: isVersionLoading,
    error: versionError
  } = useDemoFetcherWithSwr({
    serviceName: "default",
    apiName: "spot_search_engine",
    operationId: "getVersion",
    requestParameters: undefined
  });
  const {
    sender: getRecommendation,
    isLoading: isGetRecommendationLoading,
    error: getRecommendationError,
    data: getRecommendationData
  } = useDemoSender({
    serviceName: "default",
    apiName: "spot_search_engine",
    operationId: "getScoredRecommendation"
  });

  // ユーザー情報とデバッグモード
  const debugMode = useAtomValue(debugModeAtom);
  const user = useAtomValue(userAtom);

  // AIレベルのインフォメーションアイコンからオンボーディングツアーを再表示する
  const reopenOnboardingTour = useReopenOnboardingTour();

  // フィルタパラメータを取得
  const queryParams = useAtomValue(scoredRecommendationQueryParamsAtom);

  // LINE連携の状態管理
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showLineResultModal, setShowLineResultModal] = useState(false);
  const [lineConnectionSuccess, setLineConnectionSuccess] = useState(false);
  const [lineConnectionError, setLineConnectionError] = useState<"already_linked" | "other" | null>(
    null
  );
  const { refreshLineConnection } = useLineConnection();

  // 位置情報の状態
  const locationPermission = useAtomValue(locationPermissionAtom);
  const locationFallback = useAtomValue(locationFallbackAtom);
  const [isLocationErrorDialogOpen, setIsLocationErrorDialogOpen] = useState(false);
  const [isLocationPermissionModalOpen, setIsLocationPermissionModalOpen] = useState(false);

  // フィルター関連の状態
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const hasActiveFilters = useAtomValue(hasActiveFiltersAtom);
  const [sortOption, setSortOption] = useAtom(sortOptionAtom);

  // モーダルルーティング管理
  const {
    isSettingsMenuOpen,
    isUserMenuModalOpen,
    isLineDeliverySettingsModalOpen,
    handleSettingsMenuChange,
    handleUserMenuModalChange,
    handleLineDeliverySettingsModalChange,
    openSettingsMenu,
    navigateToUserInfo,
    navigateToLineSettings
  } = useModalRouting();

  // 初回読み込みフラグ
  const isInitialLoadDone = useRef(false);

  // 全体の推薦結果を管理
  const [recommendation, setRecommendation] = useState<RecommendationResult | null>(null);

  // 続きを読み込み中かどうか
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // 全ての推薦を読み込み完了したかどうか
  const [hasMoreRecommendations, setHasMoreRecommendations] = useState(true);

  // 無限スクロールでのエラー状態
  const [loadMoreError, setLoadMoreError] = useState<Error | null>(null);

  // スクロール検出とIntersection Observer用のref
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const handleGetRecommendation = useCallback(
    async (isInitialRequest: boolean = true) => {
      // 認証チェック - 推薦機能にはログインが必要
      if (!isAuthenticated(user)) {
        console.error("Recommendation requires authentication");
        setRecommendation(null);
        setHasMoreRecommendations(false);
        setLoadMoreError(new Error("Authentication required for recommendations"));
        return;
      }

      // 位置情報が利用できない場合でも、フォールバックを使用している場合は推薦を実行
      if (!locationPermission.isLocationAvailable && !locationFallback.isUsingFallback) {
        console.log(
          "位置情報が利用できず、フォールバックも設定されていないため、推薦を実行しません"
        );
        setIsLocationErrorDialogOpen(true);
        return;
      }

      if (isInitialRequest) {
        setRecommendation(null);
        setHasMoreRecommendations(true);
        setLoadMoreError(null);
        const result = await getRecommendation({ requestBody: queryParams });
        if (result) {
          setRecommendation(result);
          // request_recommendation イベント送信 (初回)
          if ((user as { userId?: string } | null)?.userId) {
            trackRequestRecommendation({
              user_id: (user as { userId: string }).userId,
              recommendation_id: result.recommendation_id,
              mood_category:
                queryParams.mood_category && queryParams.mood_category.length > 0
                  ? queryParams.mood_category.join(",")
                  : null,
              genre: Array.isArray(queryParams.genre)
                ? queryParams.genre.join(",")
                : queryParams.genre ?? null,
              subgenre: Array.isArray(queryParams.subgenre)
                ? queryParams.subgenre.join(",")
                : queryParams.subgenre ?? null,
              result_count: result.results.length,
              recommendation_iter: result.recommendation_iter
            });
          }
          // 結果が少ない場合は、これ以上の推薦がない可能性
          if (result.results.length < 10) {
            setHasMoreRecommendations(false);
          }
        }
      } else if (
        recommendation?.recommendation_id &&
        hasMoreRecommendations &&
        !isLoadingMore &&
        !loadMoreError
      ) {
        setIsLoadingMore(true);
        setLoadMoreError(null);

        const result = await getRecommendation({
          requestBody: queryParams,
          recommendationId: recommendation.recommendation_id
        });

        // 少し待ってから最新のエラー状態をチェック（useDemoSenderの状態更新を待つ）
        await new Promise((resolve) => setTimeout(resolve, 200));

        // resultがundefinedの場合はエラーとして扱う（500エラー等）
        if (result === undefined) {
          setLoadMoreError(getRecommendationError || new Error("読み込みに失敗しました"));
        } else if (result) {
          setRecommendation((prev) => {
            if (!prev) return result;
            return {
              ...result,
              results: [...prev.results, ...result.results]
            };
          });
          // request_recommendation イベント送信 (load more)
          if ((user as { userId?: string } | null)?.userId) {
            trackRequestRecommendation({
              user_id: (user as { userId: string }).userId,
              recommendation_id: result.recommendation_id,
              mood_category:
                queryParams.mood_category && queryParams.mood_category.length > 0
                  ? queryParams.mood_category.join(",")
                  : null,
              genre: Array.isArray(queryParams.genre)
                ? queryParams.genre.join(",")
                : queryParams.genre ?? null,
              subgenre: Array.isArray(queryParams.subgenre)
                ? queryParams.subgenre.join(",")
                : queryParams.subgenre ?? null,
              result_count: result.results.length,
              recommendation_iter: result.recommendation_iter
            });
          }
          // 結果が少ない場合は、これ以上の推薦がない
          if (result.results.length < 10) {
            setHasMoreRecommendations(false);
          }
        } else {
          setHasMoreRecommendations(false);
        }

        setIsLoadingMore(false);
      }
    },
    [
      getRecommendation,
      getRecommendationError,
      getRecommendationData,
      queryParams,
      recommendation?.recommendation_id,
      hasMoreRecommendations,
      isLoadingMore,
      locationPermission.isLocationAvailable,
      loadMoreError
    ]
  );

  // 初回ロード時のみ推薦APIを実行（位置情報が利用可能またはフォールバック使用時）
  useEffect(() => {
    if (
      !isInitialLoadDone.current &&
      (locationPermission.isLocationAvailable || locationFallback.isUsingFallback)
    ) {
      handleGetRecommendation(true);
      isInitialLoadDone.current = true;
    }
  }, [
    locationPermission.isLocationAvailable,
    locationFallback.isUsingFallback,
    handleGetRecommendation
  ]);

  // LINE連携のコールバック処理
  useEffect(() => {
    const lineLinked = searchParams.get("line_linked");
    const lineError = searchParams.get("line_error");

    if (lineLinked === "true") {
      // LINE連携成功
      setLineConnectionSuccess(true);
      setLineConnectionError(null);
      setShowLineResultModal(true);

      // LINE連携状態を再取得
      refreshLineConnection();

      // URLからクエリパラメータを削除
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("line_linked");
      navigate({ search: newSearchParams.toString() }, { replace: true });
    } else if (lineError) {
      // LINE連携失敗
      setLineConnectionSuccess(false);

      if (lineError === "already_linked") {
        setLineConnectionError("already_linked");
      } else {
        setLineConnectionError("other");
      }

      setShowLineResultModal(true);

      // URLからクエリパラメータを削除
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("line_error");
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  }, [searchParams, navigate, refreshLineConnection]);

  // 無限スクロール用のIntersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          entry.isIntersecting &&
          hasMoreRecommendations &&
          !isLoadingMore &&
          !loadMoreError &&
          recommendation
        ) {
          handleGetRecommendation(false);
        }
      },
      {
        root: null,
        rootMargin: "100px", // 100px手前で読み込み開始
        threshold: 0.1
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [
    hasMoreRecommendations,
    isLoadingMore,
    loadMoreError,
    recommendation,
    handleGetRecommendation
  ]);

  // フィルタ変更時に推薦をリセット
  const previousQueryParams = useRef<string>(JSON.stringify(queryParams));

  useEffect(() => {
    const currentQueryParams = JSON.stringify(queryParams);

    // フィルタが変更された場合（初回読み込み除く）
    if (isInitialLoadDone.current && previousQueryParams.current !== currentQueryParams) {
      setRecommendation(null);
      setHasMoreRecommendations(true);
      setIsLoadingMore(false);
      setLoadMoreError(null);
      handleGetRecommendation(true);
    }

    previousQueryParams.current = currentQueryParams;
  }, [queryParams, handleGetRecommendation]);

  // 初回ガイドツアー（スポットカード描画後、かつLINEモーダルを閉じた後に一度だけ起動）
  useOnboardingTour(
    isAuthenticated(user) &&
      !!recommendation &&
      recommendation.results.length > 0 &&
      !showLineResultModal &&
      !isLineDeliverySettingsModalOpen
  );

  // ヘッダー中央: ソートボタン群
  const headerCenter = (
    <div className="flex items-center justify-center gap-2">
      <Button
        variant={sortOption === "distance" ? "default" : "ghost"}
        className="relative rounded-full"
        onClick={() => setSortOption("distance")}
      >
        距離
      </Button>
      <Button
        variant={sortOption === "popularity" ? "default" : "ghost"}
        className="relative rounded-full"
        onClick={() => setSortOption("popularity")}
      >
        人気
      </Button>
    </div>
  );

  // ヘッダー右側: 位置情報インジケータ + フィルターボタン + 設定ボタン
  const headerRight = (
    <div className="flex items-center gap-2">
      <Button size="icon" variant="ghost" onClick={openSettingsMenu} aria-label="設定">
        <Settings />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="relative"
        onClick={() => setIsFilterDrawerOpen(true)}
      >
        <SlidersHorizontalIcon />
        {hasActiveFilters && (
          <div className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-orange-500" />
        )}
      </Button>
    </div>
  );

  return (
    <PageWrapper headerCenter={headerCenter} headerRight={headerRight}>
      <Helmet>
        <title>ホーム - Refloop</title>
      </Helmet>
      {debugMode && (
        <div className="flex items-center justify-between gap-2 rounded-md bg-gray-100 p-2">
          <span className="text-sm">{user ? `user id: ${user.userId}` : "Not logged in"}</span>
          {user && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const { signOut } = await import("aws-amplify/auth");
                await signOut();
                window.location.reload();
              }}
            >
              ログアウト
            </Button>
          )}
        </div>
      )}
      {isAuthenticated(user) && (
        <div className="mb-2">
          <AiLevelDisplay onInfoClick={reopenOnboardingTour} />
        </div>
      )}
      <div className="grid gap-2">
        {!isAuthenticated(user) ? (
          <AuthenticationError
            title="ログインが必要です"
            message="スポットの推薦機能をご利用いただくには、アカウントにログインしてください。"
            icon={<MapPin className="h-12 w-12" />}
          />
        ) : locationPermission.state === "checking" ||
          (!recommendation && !getRecommendationError && !isInitialLoadDone.current) ? (
          <div className="flex h-96 flex-col items-center justify-center gap-4">
            <MapPin className="h-12 w-12 animate-pulse text-blue-500" />
            <span className="">位置情報を取得中...</span>
          </div>
        ) : isGetRecommendationLoading && !recommendation ? (
          <div className="flex h-96 flex-col items-center justify-center gap-4">
            <img src="/logo-icon.svg" className="h-12 w-12 animate-bounce" />
            <span className="">提案中</span>
          </div>
        ) : getRecommendationError && !recommendation ? (
          <div className="flex h-96 flex-col items-center justify-center gap-4">
            <div>😵‍💫 提案に失敗しました</div>
            <Button
              variant="outline"
              className="mt-2"
              onClick={() => handleGetRecommendation(true)}
            >
              再読み込み
            </Button>
          </div>
        ) : recommendation && recommendation.results.length > 0 ? (
          <>
            {recommendation.results.map((result, index: number) => (
              <SwipeableCard
                key={result.spot_id}
                // SwipeableCard固有のprops
                recommendationId={recommendation.recommendation_id}
                spotName={result.name}
                spotDistance={result.distance ?? undefined}
                cardPosition={index + 1}
                totalRecommendations={recommendation.results.length}
                // SpotCardのprops
                spotId={result.spot_id}
                name={result.name}
                googleCid={result.google_cid ?? undefined}
                distance={result.distance ?? undefined}
                savedTotalCount={result.saved_total_count}
                visitedTotalCount={result.visited_total_count}
                showCount
                showBadge={(result.curation_score ?? 0) > 0}
                tourAnchor={index === 0}
              />
            ))}
            {/* 無限スクロール用のトリガー要素 */}
            <div ref={loadMoreRef} className="h-4" />

            {isLoadingMore && (
              <div className="flex justify-center p-4">
                <img src="/logo-icon.svg" className="h-8 w-8 animate-bounce" />
                <span className="ml-2 text-sm">読み込み中...</span>
              </div>
            )}

            {loadMoreError && (
              <div className="flex flex-col items-center justify-center gap-3 p-4">
                <div className="text-center">
                  <div className="text-sm text-red-600">😵‍💫 続きの読み込みに失敗しました</div>
                  <div className="mt-1 text-xs text-gray-500">{loadMoreError.message}</div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setLoadMoreError(null);
                    handleGetRecommendation(false);
                  }}
                >
                  再試行
                </Button>
              </div>
            )}

            {!hasMoreRecommendations &&
              !loadMoreError &&
              recommendation &&
              recommendation.results.length > 0 && (
                <div className="flex justify-center p-4">
                  <span className="text-sm text-gray-500">全ての推薦を表示しました</span>
                </div>
              )}
          </>
        ) : (
          <div className="flex h-96 flex-col items-center justify-center gap-4">
            <div className="text-center">
              <div className="text-lg">😔 条件に合うスポットが見つかりませんでした</div>
              <div className="mt-2 text-sm text-gray-500">フィルター条件を変更してみてください</div>
            </div>
            <Button variant="outline" onClick={() => handleGetRecommendation(true)}>
              再度提案する
            </Button>
          </div>
        )}
      </div>
      {debugMode && (
        <div className="mt-4 p-4 text-center ">
          {versionError
            ? `Error: ${versionError.message}`
            : version
            ? `api ver. ${version}`
            : isVersionLoading
            ? "api ver. Loading..."
            : "No data"}
        </div>
      )}

      {/* フィルタDrawer */}
      <RecommendationFilterDrawer open={isFilterDrawerOpen} onOpenChange={setIsFilterDrawerOpen} />

      {/* 位置情報フォールバックバナー */}
      {locationFallback.isUsingFallback && (
        <LocationFallbackBanner onTap={() => setIsLocationPermissionModalOpen(true)} />
      )}

      {/* 位置情報取得拒否モーダル */}
      <LocationPermissionModal
        open={isLocationPermissionModalOpen}
        onSkip={() => setIsLocationPermissionModalOpen(false)}
        onReload={() => window.location.reload()}
      />

      {/* 位置情報エラーDialog */}
      <LocationErrorDialog
        open={isLocationErrorDialogOpen}
        onOpenChange={setIsLocationErrorDialogOpen}
      />

      {/* 設定メニュー */}
      <SettingsMenu
        open={isSettingsMenuOpen}
        onOpenChange={handleSettingsMenuChange}
        onNavigateToUserInfo={navigateToUserInfo}
        onNavigateToLineSettings={navigateToLineSettings}
      />

      {/* ユーザー情報モーダル */}
      <UserMenuModal open={isUserMenuModalOpen} onOpenChange={handleUserMenuModalChange} />

      {/* LINE配信設定モーダル */}
      <LineDeliverySettingsModal
        open={isLineDeliverySettingsModalOpen}
        onOpenChange={handleLineDeliverySettingsModalChange}
      />

      {/* LINE連携結果モーダル */}
      <LineConnectionResultModal
        open={showLineResultModal}
        onOpenChange={setShowLineResultModal}
        success={lineConnectionSuccess}
        errorType={lineConnectionError ?? undefined}
      />
    </PageWrapper>
  );
};
